package com.dynamicruntime.webapp

import com.dynamicruntime.common.endpoint.EI
import com.dynamicruntime.common.gedra.DSV
import com.dynamicruntime.common.endpoint.EP
import com.dynamicruntime.common.gedra.GDF
import com.dynamicruntime.common.gedra.GE
import com.dynamicruntime.common.gedra.GSRC
import com.dynamicruntime.common.gedra.headingLine
import com.dynamicruntime.common.gedra.workflow.SVY
import com.dynamicruntime.common.gedra.workflow.WDSP
import com.dynamicruntime.common.gedra.workflow.WFC
import com.dynamicruntime.common.gedra.workflow.WFD
import com.dynamicruntime.common.gedra.workflow.WFS
import com.dynamicruntime.common.gedra.workflow.WSF
import com.dynamicruntime.common.gedra.workflow.WVF
import com.dynamicruntime.common.gedra.workflow.WfEntry
import com.dynamicruntime.common.gedra.workflow.WfPhase
import com.dynamicruntime.common.schema.SCH
import com.dynamicruntime.common.schema.SLDM
import com.dynamicruntime.common.schema.SchFailCode
import com.dynamicruntime.common.schema.SchFailure
import com.dynamicruntime.common.schema.SchLayout
import com.dynamicruntime.common.schema.SchLayoutMode
import com.dynamicruntime.common.schema.SchLayoutField
import com.dynamicruntime.common.schema.SchType
import com.dynamicruntime.common.schema.parseDeliveredLayouts
import com.dynamicruntime.common.schema.parseSchemaTypes
import com.dynamicruntime.common.schema.refName
import com.dynamicruntime.common.util.analyzeTemplate
import com.dynamicruntime.common.util.formatDayPart
import com.dynamicruntime.common.util.parseDateOrNull
import com.dynamicruntime.common.util.toJsonListOfMaps
import com.dynamicruntime.common.util.toJsonListOfStrings
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import com.dynamicruntime.common.util.toOptStr
import com.dynamicruntime.common.util.humanizeFieldName
import com.dynamicruntime.common.gedra.workflow.LockNames
import com.dynamicruntime.common.gedra.workflow.TraitLockCopy
import com.dynamicruntime.common.gedra.workflow.WfSaveKind

/**
 * The frontend's model of a resolved workflow (issue #536) — the `/gedra/workflow/view` response parsed into what
 * the create page, and the edit page for a survey or a normal workflow, renders. **Self-contained**: each trait's
 * schema comes from the view's own `$defs`, resolved here with the same kernel `parseSchemaTypes` the endpoint
 * catalog uses, so the page needs no second fetch and a workflow that narrows a trait renders the narrowed shape.
 *
 * These are pure maps-in, model-out functions with no React and no server, so they carry the `jsNodeTest`
 * coverage the issue asks for; the component that renders them is driven in a browser.
 */
class WfTraitView(
    val traitId: String,
    val required: Boolean,
    /** The trait's data type, resolved from the view's `$defs` — what a field renders and validates against. */
    val type: SchType,
    /** The qualified name [type] was resolved under -- the key the view's `fieldLayouts` are joined on. */
    val typeName: String,
    /**
     * The data type's field layout (issue #585), joined from the view's `fieldLayouts` by [typeName]; null when
     * the type declares none, and the trait then renders from its schema alone.
     */
    val fieldLayout: SchLayout?,
)

/**
 * The layout `label` a trait's section shows as its heading on a workflow form (issues #1520, #1534), inline Markdown
 * and all, its heading marks taken off -- or null when it says nothing (none, blank, or marks alone, issue #1541), and
 * the trait's name by the rule (`traitNameOf`) stands in, as on every other page. Judged by `headingLine`, not
 * `headingName`: a label holding a `${…}` template is kept, since the form is the one place that fills it in. Pure.
 */
fun traitSectionLabel(trait: WfTraitView): String? =
    trait.fieldLayout?.label?.takeIf { headingLine(it).isNotBlank() }?.trimStart('#')?.trim()

/** One save option a task offers: what a button says and what it does (`WfSaveKind.name`, e.g. create/edit). */
class WfSaveView(val id: String, val label: String, val kind: String)

/**
 * One content failure a task's data has (issue #700): which trait, the field [path] within its data, and the
 * wording to show -- the schema author's `userMessage` when the field declares one, else the validator's
 * `message`, the same rule a reported failure is read by anywhere else.
 */
class WfProblem(val traitId: String, val path: String, val message: String)

/**
 * A task's status for the task rail (issue #700), as the view computed it: presence ([complete], [missingTraits])
 * and content ([valid], [invalidTraits], [problems]) kept apart, so "needs information" and "has a problem" draw
 * as different marks. Null on a task the view carried none for.
 */
class WfTaskStatus(
    val complete: Boolean,
    val valid: Boolean,
    val missingTraits: List<String>,
    val invalidTraits: List<String>,
    val problems: List<WfProblem>,
)

/**
 * One task of the workflow: its traits in the order the page draws them, its saves, and — when the view was
 * resolved against an existing form (a survey edit, issue #659) — that task's **current entries**, the source a
 * page seeds its fields from. A creation view carries none, so [entries] defaults empty.
 */
class WfTaskView(
    val id: String,
    val label: String,
    val traits: List<WfTraitView>,
    val saves: List<WfSaveView>,
    val entries: List<Map<String, Any?>> = emptyList(),
    /** The task's status for the rail (issue #700), as the view computed it; null when it carried none. */
    val status: WfTaskStatus? = null,
    /**
     * Whether this caller may save the task (issue #856), by its rule for who may -- the rule the save endpoint
     * enforces. True when the view says nothing (creation, survey, or a task with no rule).
     */
    val canSave: Boolean = true,
    /**
     * How the task is shown (issue #788): the one branch of its display selector that applies to this caller, chosen
     * on the backend -- the page never sees the others or their conditions. Null when the task declares no display,
     * which means its own rendering.
     */
    val display: WfTaskDisplay? = null,
    /** An approval task's approval (issue #787), as the view resolved it; null on any other task. */
    val approval: WfApprovalView? = null,
    /** The task's facts for this caller (issues #786, #788) -- `kdr:reviewer` for a reviewer, `kdr:isCta`, and so on. */
    val facts: Set<String> = emptySet(),
) {
    /** Whether this caller is a reviewer of the task (issue #787): what an approval's button is shown to. */
    val isReviewer: Boolean get() = WFC.reviewer in facts

    /** Whether the task is shown disabled (issue #788): greyed, nothing in it actionable, its rail entry inert. */
    val isDisabled: Boolean get() = display?.disabled == true
}

/**
 * One task display branch (issue #788), as the view delivers it: its [mode] (`WDSP.textMode` or `WDSP.defaultMode`),
 * the [text] a text branch shows -- `%{…}` already resolved, `${'$'}{…}` left for [displayTextOf] -- and whether the
 * task is [disabled].
 */
class WfTaskDisplay(val mode: String, val text: String?, val disabled: Boolean)

/**
 * An approval task's approval (issue #787): the backend-passed [prompt] and [button] copy, whether it is [approved],
 * and once it is, [approvedByName] (the approver's profile name, else their email, issue #1430) and [approvedAt]. [data] is the whole block as it
 * arrived -- what a display's `${'$'}{…}` placeholders are filled from.
 */
class WfApprovalView(
    val prompt: String,
    val button: String,
    val approved: Boolean,
    val approvedByName: String?,
    val approvedAt: String?,
    val data: Map<String, Any?>,
)

/**
 * A resolved workflow view, ready to render (issue #536, #659). It serves the **creation** workflow (no form yet),
 * and a **survey** or a **normal** workflow (issue #791) resolved against an existing form (each task seeded from its
 * current [WfTaskView.entries]); [entry] says which (`WfEntry.name`, e.g. `"creation"`/`"survey"`/`"normal"`).
 */
class WorkflowView(
    val workflowId: String,
    val entry: String,
    val showTaskList: Boolean,
    val tasks: List<WfTaskView>,
    /**
     * What the workflow is called (issue #719), resolved like a task's label -- the page's title over the form.
     * Empty when the definition gives none, and the page then uses its own generic title.
     */
    val label: String = "",
    /**
     * Markdown help shown under the title (issue #1221) -- copy kept beside the workflow rather than in it, so a
     * deployment can explain a workflow without changing it. Empty for none.
     */
    val help: String = "",
    /**
     * The caller's frontend-delivered cfacts (issue #569), `name -> present`, which each rendered trait's
     * [SchemaForm] evaluates a property's `g-visibleWhen` against — so an admin-only field is hidden from an
     * ordinary caller. The same shape the endpoint catalog delivers.
     */
    val cfacts: Map<String, Boolean>,
    /**
     * The per-type field layouts the view delivered (issue #585), keyed by qualified type name like its `$defs` --
     * the same shape the endpoint catalog carries. Each trait's own is already joined onto [WfTraitView.fieldLayout];
     * this is the whole closure, for a renderer that reaches a nested type by name.
     */
    val fieldLayouts: Map<String, SchLayout> = emptyMap(),
    /**
     * The earliest task still needing action (issue #700) -- the first, in order, whose status is incomplete or
     * invalid -- or null when every task is done. What the rail opens on when the URL names no task.
     */
    val focusTask: String? = null,
    /** A normal workflow's [WfPhase] name (issue #790); null for creation and survey. */
    val phase: String? = null,
    /** For a normal workflow viewed against a form: whether the form is engaged with it (issue #791). */
    val engaged: Boolean? = null,
    /** For a normal workflow the form is not engaged with: whether it passes the eligibility tests (issue #791). */
    val eligible: Boolean? = null,
    /** When it does not: why, resolved -- what the page lists in place of an Engage that would fail. */
    val ineligibleReasons: List<String> = emptyList(),
    /**
     * The form's traits locked for this caller (issue #857), by trait id -- each with every lock on it, since two
     * workflows may lock one trait; drawn read-only and left out of a save.
     */
    val lockedTraits: Map<String, List<TraitLock>> = emptyMap(),
    /**
     * Whether the whole form is read-only for this caller (issue #1433): kept to its owner, and the caller an editor who
     * is neither its owner nor a reviewer of its current work. Every task then arrives unsaveable.
     */
    val keptToOwner: Boolean = false,
    /**
     * The Design View block (issue #972): where the workflow and each type the page draws were declared. Present only
     * when this caller asked for Design View and the backend honored it -- null on every ordinary view.
     */
    val design: WfDesign? = null,
) {
    /** A normal workflow (issue #791): one a form is put into, rather than the creation or the survey. */
    val isNormal: Boolean get() = entry == WfEntry.normal.name

    /**
     * Whether this normal workflow's tasks may be worked on now: engaged, still calculated (not frozen), and with at
     * least one task this caller can edit ([isEditable]).
     */
    val canWork: Boolean get() = !isNormal || (engaged == true && (phase == WfPhase.relevant.name || phase == WfPhase.engageable.name) && tasks.any { isEditable(it, isEdit = true) })

    /**
     * Whether [task] is one this caller can edit here: it offers a save of the page's kind -- an `edit` against a form
     * ([isEdit]), else a `create` (issue #817) -- they may make it (issue #856), it is not shown disabled (issue #832),
     * and not every trait it collects is locked for them (issue #857). What decides its fields, its Save and the page's
     * Edit. [isEdit] has no default: a create page and a form page answer differently, and a forgotten argument must
     * not quietly pick one.
     */
    fun isEditable(task: WfTaskView, isEdit: Boolean): Boolean =
        task.canSave && !task.isDisabled && saveOfKind(task, isEdit) != null &&
            (task.traits.isEmpty() || task.traits.any { it.traitId !in lockedTraits })

    /**
     * Whether the page offers Edit (issue #857 UI pass): the workflow can be worked on, and -- when one task is shown
     * at a time ([shown]) -- that task is editable, so Edit never leads to a step that is still read-only. Null
     * [shown] means every task is on the page, and then one of them has to be (issue #817: a creation workflow
     * opened against a form has none).
     */
    fun offersEdit(shown: WfTaskView?): Boolean =
        canWork && (shown?.let { isEditable(it, isEdit = true) } ?: tasks.any { isEditable(it, isEdit = true) })

    /**
     * Whether the form may be put into this normal workflow from here: not yet engaged, eligible, and engagement is open.
     *
     * Offering Engage here -- on the form's own page, once it is eligible, which for a workflow that tests the survey
     * means once the survey is complete -- is the current **best-guess presentation**, not a direction (issue #829): a
     * large client may well need a different UI, and an API integration may engage a form as soon as the engage
     * endpoint allows. The endpoint's gates -- the engagement window and the eligibility tests -- are the rule; this
     * only mirrors them, so a page does not offer what the endpoint would refuse.
     */
    val canEngage: Boolean get() =
        isNormal && engaged == false && eligible == true && phase == WfPhase.engageable.name && !keptToOwner
}

/** A task's [WVF.status] map as a [WfTaskStatus], or null when the task carried none. */
private fun parseTaskStatus(raw: Any?): WfTaskStatus? {
    val s = raw.toJsonMapOrEmpty()
    if (s.isEmpty()) return null
    return WfTaskStatus(
        complete = s[SVY.complete] == true,
        valid = s[SVY.valid] == true,
        missingTraits = s[SVY.missingTraits].toJsonListOfStrings(),
        invalidTraits = s[SVY.invalidTraits].toJsonListOfStrings(),
        // Each problem is the kernel's own failure wire map (`SchFailure.toWireMap`) plus the trait it belongs to,
        // read by the same constants every other reported failure is.
        problems = s[WVF.problems].toJsonListOfMaps().map {
            WfProblem(
                traitId = it[GE.traitId].toOptStr() ?: "",
                path = it[EP.failurePath].toOptStr() ?: "",
                message = it[EP.failureUserMessage].toOptStr() ?: it[EP.failureMessage].toOptStr() ?: "",
            )
        },
    )
}

/**
 * Parses a `/gedra/workflow/view` `results` map into a [WorkflowView], or **null** when the caller has no such
 * workflow (`found=false`) — the signal the page uses (a create page falls back to the trait picker; a survey
 * edit page reports there is no survey). Each trait's `schemaRef` is resolved against the view's `$defs`; a ref
 * that names no carried type is a fault, since the view is supposed to carry every type it points at.
 */
fun parseWorkflowView(results: Map<String, Any?>): WorkflowView? {
    if (results[WVF.found] != true) return null
    val defTypes = parseSchemaTypes(results[SCH.dDefs].toJsonMapOrEmpty())
    // The third closure (issue #585), keyed like `$defs`; a trait's layout is the entry under its type name.
    val fieldLayouts = parseDeliveredLayouts(results[WVF.fieldLayouts])
    val tasks = results[WFD.tasks].toJsonListOfMaps().map { t ->
        WfTaskView(
            id = t[WFD.id].toOptStr() ?: "",
            label = t[WFD.label].toOptStr() ?: "",
            traits = t[WFD.traits].toJsonListOfMaps().map { tr ->
                val ref = tr[WVF.schemaRef].toOptStr() ?: ""
                val name = refName(ref)
                    ?: error($$"Workflow view trait '$${tr[WFD.traitId]}' has a schemaRef '$$ref' that is not a local $defs pointer.")
                val type = defTypes[name]
                    ?: error($$"Workflow view references '$$name', which its own $defs does not carry.")
                WfTraitView(tr[WFD.traitId].toOptStr() ?: "", tr[WFD.required] == true, type, name, fieldLayouts[name])
            },
            saves = t[WFD.saves].toJsonListOfMaps().map { s ->
                WfSaveView(s[WFD.id].toOptStr() ?: "", s[WFD.label].toOptStr() ?: "", s[WFD.kind].toOptStr() ?: "")
            },
            // Present only for a survey view resolved against a form (issue #659) -- the seed for each field.
            entries = t[WVF.entries].toJsonListOfMaps(),
            status = parseTaskStatus(t[WVF.status]),
            canSave = t[WVF.canSave] != false,
            display = parseTaskDisplay(t[WFD.display]),
            approval = parseApproval(t[WVF.approval]),
            facts = t[WVF.facts].toJsonListOfStrings().toSet(),
        )
    }
    val cfacts = results[WVF.cfacts].toJsonMapOrEmpty().mapValues { it.value == true }
    return WorkflowView(
        workflowId = results[WFD.workflowId].toOptStr() ?: "",
        entry = results[WFD.entry].toOptStr() ?: "",
        showTaskList = results[WVF.showTaskList] == true,
        tasks = tasks,
        label = results[WFD.label].toOptStr().orEmpty(),
        help = results[WVF.help].toOptStr().orEmpty(),
        cfacts = cfacts,
        fieldLayouts = fieldLayouts,
        focusTask = results[WVF.focusTask].toOptStr(),
        phase = results[WVF.phase].toOptStr(),
        engaged = results[WVF.engaged] as? Boolean,
        eligible = results[WVF.eligible] as? Boolean,
        lockedTraits = parseTraitLocks(results[WVF.lockedTraits]).groupBy { it.traitId },
        keptToOwner = results[WVF.keptToOwner] == true,
        ineligibleReasons = results[WVF.ineligibleReasons].toJsonListOfStrings(),
        design = parseWfDesign(results[DSV.designBlock]),
    )
}

/** A task's delivered display branch (issue #788), or null when it carried none -- the task's own rendering. */
fun parseTaskDisplay(raw: Any?): WfTaskDisplay? {
    val m = raw as? Map<*, *> ?: return null
    val d = m.toJsonMapOrEmpty()
    return WfTaskDisplay(
        mode = d[WDSP.mode].toOptStr() ?: WDSP.defaultMode,
        text = d[WDSP.text].toOptStr(),
        disabled = d[WDSP.disabled] == true,
    )
}

/** An approval task's delivered approval (issue #787), or null on a task that is not one. */
fun parseApproval(raw: Any?): WfApprovalView? {
    val m = raw as? Map<*, *> ?: return null
    val a = m.toJsonMapOrEmpty()
    return WfApprovalView(
        prompt = a[WFD.prompt].toOptStr().orEmpty(),
        button = a[WFD.button].toOptStr().orEmpty(),
        approved = a[WVF.approved] == true,
        approvedByName = a[WVF.approvedByName].toOptStr(),
        approvedAt = a[WFS.approvedAt].toOptStr(),
        data = a,
    )
}

/**
 * The text a task shows in place of its own rendering (issue #788), or null when its display is not a text branch.
 * `%{…}` arrived resolved; `${'$'}{…}` is filled here by the kernel's `analyzeTemplate` from the task's own view data -- its
 * approval block, so "approved by `${'$'}{approvedByName}`" names the approver. The approver's name is not always there
 * (their account since removed), so it reads "a reviewer" when absent; and a placeholder nothing can fill is dropped,
 * never shown as template syntax, rather than failing the page. Pure, and covered under `jsNodeTest`.
 */
fun displayTextOf(task: WfTaskView): String? {
    val display = task.display?.takeIf { it.mode == WDSP.textMode } ?: return null
    val text = display.text ?: return null
    val data = mapOf<String, Any?>(WVF.approvedByName to "a reviewer") + (task.approval?.data ?: emptyMap()).filterValues { it != null }
    return text.analyzeTemplate(evaluateWith = data).value ?: text.replace(unfilledPlaceholder, "")
}

/** A `${'$'}{…}` block, for dropping one [displayTextOf] could not fill. */
private val unfilledPlaceholder = Regex("""\$\{[^}]*\}""")

/**
 * The line an approved approval task shows in its own rendering (issue #787): who approved it and on what day, as far
 * as the view says. A name that is an email -- an approver with no name on their profile -- is left out (issue #1430):
 * no author chose this wording, and the reader may not be one who should see it. A workflow's own display text can
 * show it, deciding with `contains(approvedByName, "@")`. Pure, and covered under `jsNodeTest`.
 */
fun approvedLine(approval: WfApprovalView): String {
    val day = approval.approvedAt?.let { it.parseDateOrNull()?.formatDayPart() }
    val name = approval.approvedByName?.takeUnless { "@" in it }
    return "Approved" + (name?.let { " by $it" } ?: "") + (day?.let { " on $it" } ?: "") + "."
}

/**
 * The save [task] offers for the page's mode (issue #817): its `edit` save against an existing form ([isEdit]), else
 * its `create` save -- never another kind in their place, and null when it has none. A creation task opened against
 * a form, or a normal task that declares no save, therefore draws no Save rather than posting the wrong one. Pure,
 * and covered under `jsNodeTest`.
 */
fun saveOfKind(task: WfTaskView, isEdit: Boolean): WfSaveView? {
    val wanted = if (isEdit) WfSaveKind.edit.name else WfSaveKind.create.name
    return task.saves.firstOrNull { it.kind == wanted }
}

/**
 * The task the survey page opens on (issue #700): the one the URL names when it is a task of the view, else the
 * view's earliest task needing action, else the first task. Null only for a view with no tasks.
 */
fun initialTaskFor(view: WorkflowView, requested: String?): String? {
    val ids = view.tasks.map { it.id }
    return requested?.takeIf { it in ids } ?: view.focusTask?.takeIf { it in ids } ?: ids.firstOrNull()
}

/**
 * The mark a rail entry draws for a task (issue #700). Absence and invalidity are different marks: a task missing
 * a required trait is [incomplete] whatever else is wrong with it -- the orange check means "complete, but with a
 * problem", so it must not be drawn on a task that is not complete; its problems still reach the tooltip.
 */
@Suppress("EnumEntryName")
enum class RailMark { incomplete, invalid, complete }

fun railMark(status: WfTaskStatus?): RailMark = when {
    status == null || !status.complete -> RailMark.incomplete
    !status.valid -> RailMark.invalid
    else -> RailMark.complete
}

/**
 * What a rail mark means, in words, for the entry's tooltip and screen-reader label (issue #700): unsaved edits,
 * the required traits still missing (named by [nameOf] -- the rail passes the same heading the panel uses, so
 * the two agree; the default humanizes the id), each friendly problem, or -- when nothing else applies --
 * "Complete". One line each, in that order.
 */
fun railExplanation(status: WfTaskStatus?, unsaved: Boolean, nameOf: (String) -> String = ::humanizeFieldName): String {
    val lines = buildList {
        if (unsaved) add("Unsaved changes")
        if (status == null || !status.complete) {
            val missing = status?.missingTraits.orEmpty()
            add(if (missing.isEmpty()) "Not started" else "Needs information: " + missing.joinToString(", ") { nameOf(it) })
        }
        status?.problems?.forEach { add(it.message) }
        if (isEmpty()) add("Complete")
    }
    return lines.joinToString("\n")
}

/**
 * Whether a task has edits not yet saved (issue #700): any of its traits' working [values] differ from the last
 * [stored] ones. Client-side only -- the backend has no notion of an unsaved draft -- and orthogonal to the
 * status mark, so the rail overlays it as a badge rather than drawing it as a fourth state. The same answer
 * gates the task's Save (issue #717): a task with nothing unsaved has nothing to save.
 *
 * Compared through [comparableValues] (issue #718): a text box holds what was typed as a string against a
 * stored number, and a cleared box holds `""` against an absent key, so a raw comparison read a value typed
 * back as its original, or a field emptied that was never set, as an edit.
 */
fun taskUnsaved(
    task: WfTaskView,
    values: Map<String, Map<String, Any?>>,
    stored: Map<String, Map<String, Any?>>,
): Boolean = task.traits.any { trait ->
    val working: Map<String, Any?> = values[trait.traitId] ?: emptyMap()
    val kept: Map<String, Any?> = stored[trait.traitId] ?: emptyMap()
    comparableValues(working) != comparableValues(kept)
}

/**
 * A form's values in a shape two copies can be compared in (issue #718), whichever side came from a widget and
 * which from the wire: a null or blank string is dropped (what `emptyIsAbsent` makes of it on the way out), a
 * scalar becomes its text (`"2025"` typed and `2025` stored are one value), and maps and lists are walked. A
 * comparison aid only -- nothing sent is built from this.
 */
fun comparableValues(values: Map<String, Any?>): Map<String, Any?> = buildMap {
    for ((k, v) in values) {
        val c = comparableValue(v) ?: continue
        put(k, c)
    }
}

private fun comparableValue(value: Any?): Any? = when (value) {
    null -> null
    is String -> value.takeIf { it.isNotBlank() }
    is Map<*, *> -> comparableValues(value.toJsonMapOrEmpty())
    is List<*> -> value.map { comparableValue(it) }
    else -> value.toString()
}

/**
 * A task's status computed from its **working** values (issue #718), for the rail while the task holds unsaved
 * edits -- the client's projection of the verdict the server will give once they are saved, by the server's
 * own rule (`WfEngine.missingTraits`, `SurveyStateDeriver` and its `surveyContentFailures`) so a save does not
 * flip the mark. Presence is an **entry being there, not its data being filled in** (issue #826): a save sends
 * every trait of the task ([workflowSaveEntries]), an untouched one as `{}`, and a present-but-empty entry
 * satisfies a required trait -- what a trait *requires of its content* is its schema's `required` fields. So an
 * empty required trait is judged like any other, by its schema, rather than counted missing for being empty.
 * Content is the kernel's failures on the data, worded as a reported problem is (the author's `userMessage` first).
 *
 * One reading differs, on purpose. The server sets a `missingRequired` failure aside, because a stored entry
 * passed its save and is present whatever it lacks. Here the data is *unsaved*: a required field left empty is
 * exactly the "needs information" the mark exists to show, so it makes the trait **missing** rather than
 * invalid -- and since the save refuses it, the server never sees that state and the two cannot disagree over
 * a saved form.
 */
fun localTaskStatus(
    task: WfTaskView,
    values: Map<String, Map<String, Any?>>,
    /** The workflow's field layouts, whose form requirements count as the server's do (issue #1022). */
    layouts: Map<String, SchLayout> = emptyMap(),
): WfTaskStatus {
    val missing = mutableListOf<String>()
    val problems = mutableListOf<WfProblem>()
    for (trait in task.traits) {
        val working = values[trait.traitId] ?: emptyMap()
        // An optional trait left empty has nothing to judge. A required one is judged by its schema, empty or not.
        if (!trait.required && comparableValues(working).isEmpty()) continue
        val failures = checkFormInput(trait.type, working, layouts).failures
        if (failures.any { it.code == SchFailCode.missingRequired }) missing.add(trait.traitId)
        failures.filter { it.code != SchFailCode.missingRequired }
            .forEach { problems.add(WfProblem(trait.traitId, it.path, it.userMessage ?: it.message)) }
    }
    return WfTaskStatus(
        complete = missing.isEmpty(),
        valid = problems.isEmpty(),
        missingTraits = missing,
        invalidTraits = problems.map { it.traitId }.distinct(),
        problems = problems,
    )
}

/**
 * The failures a task's panel shows for one trait (issue #718): before the trait has been checked as a whole (a
 * Save, or the task being left), only those on fields the user has **committed** -- a first blur must not flag
 * every untouched required field below it; after, all of them. A failure on a field beneath a committed one
 * (an element of a list the user touched) counts as committed.
 */
fun shownFailures(all: List<SchFailure>, committed: Set<String>, wholeTraitChecked: Boolean): List<SchFailure> =
    if (wholeTraitChecked) all else all.filter { f -> committed.any { c -> f.path == c || f.path.startsWith("$c.") || f.path.startsWith("$c[") } }

/**
 * Whether a stored entry is a **supplied default** (`source=prefill`, issue #679/#710), not entered data.
 *
 * The one seam where presentation asks "is this a default" (issue #829). The defaults design deliberately kept the
 * presentation -- the chip, "Use it", the reset, the summary line -- off any particular source: `prefill` is the only
 * supplier today, but others may come, and a new one joins by being recognised here, not at each place that presents
 * a default.
 */
private fun isPrefillEntry(entry: Map<String, Any?>): Boolean = entry[GE.source].toOptStr() == GSRC.prefill

/**
 * How a supplied default for [field] of [trait] is presented (issue #709/#710), from the trait's `g-layout`
 * [SchLayoutField.defaultMode]; [SLDM.filled] when the layout says nothing -- the surface's fallback lives here,
 * so a layout carries the mode only to override it.
 */
fun defaultModeOf(trait: WfTraitView, field: String): String =
    trait.fieldLayout?.fieldFor(field)?.defaultMode ?: SLDM.filled

/**
 * How a resolved view's **supplied defaults** are presented (issue #710). A default is any entry with
 * `source=prefill`; each of its fields is handled by that field's [defaultModeOf]:
 *  - **`filled`** seeds the working value ([working]) -- shown in the control, marked as a default;
 *  - **`offer`** does *not* seed; its value is held in [offered] for a "use it" link, so the control stays empty.
 *
 * [modes] records the mode of every defaulted field, so the form can mark it. Entered (`source=user`) entries go
 * whole into [working], as before -- a real value is never a default.
 */
class PrefillPresentation(
    val working: Map<String, Map<String, Any?>>,
    val offered: Map<String, Map<String, Any?>>,
    val modes: Map<String, Map<String, String>>,
)

/** Splits [view]'s entries into the [PrefillPresentation] the form seeds and marks from (issue #710). */
fun prefillPresentationOf(view: WorkflowView): PrefillPresentation {
    val working = LinkedHashMap<String, Map<String, Any?>>()
    val offered = LinkedHashMap<String, Map<String, Any?>>()
    val modes = LinkedHashMap<String, Map<String, String>>()
    for (task in view.tasks) {
        val traitById = task.traits.associateBy { it.traitId }
        for (entry in task.entries) {
            val traitId = entry[GE.traitId].toOptStr() ?: continue
            val data = entry[GE.data].toJsonMapOrEmpty()
            if (!isPrefillEntry(entry)) {
                working[traitId] = data
                continue
            }
            val trait = traitById[traitId]
            val filled = LinkedHashMap<String, Any?>()
            val offer = LinkedHashMap<String, Any?>()
            val fieldModes = LinkedHashMap<String, String>()
            for ((field, value) in data) {
                val mode = trait?.let { defaultModeOf(it, field) } ?: SLDM.filled
                fieldModes[field] = mode
                if (mode == SLDM.offer) offer[field] = value else filled[field] = value
            }
            if (filled.isNotEmpty()) working[traitId] = filled
            if (offer.isNotEmpty()) offered[traitId] = offer
            modes[traitId] = fieldModes
        }
    }
    return PrefillPresentation(working, offered, modes)
}

/**
 * Every task's seed values in one map, keyed by trait id (trait ids are unique across a workflow's tasks) -- what
 * the form starts from, and what it re-snapshots from the view the page re-reads after a survey edit save (issue
 * #827). Supplied defaults are split by mode ([prefillPresentationOf]): a `filled` default seeds, an `offer` one
 * does not (it is offered, not entered), so the seed and the presented form never disagree about "unsaved".
 */
fun seedValuesOf(view: WorkflowView): Map<String, Map<String, Any?>> = prefillPresentationOf(view).working

/** Whether a value reads as empty for a supplied default (absent, or text a user has cleared) -- an unapplied
 *  `offer`, the same reading [SchemaForm] uses to decide whether to draw the "Use it" link (issue #710). */
private fun isBlankPrefillValue(value: Any?): Boolean = value == null || (value is String && value.isBlank())

/**
 * How many of [task]'s supplied defaults are still **pending** (issue #710): a `filled` field the user has not
 * yet touched (still in [suggestedByTrait]), or an `offer` field not yet applied (its value in [valuesByTrait]
 * still empty). This is the count the "N fields filled from your account" line reports, and it falls to zero as
 * the user accepts, edits, or saves them -- so the line disappears once nothing is left to confirm. Pure, so a
 * `jsNodeTest` can pin it; the React form owns the state it reads.
 */
fun pendingDefaultCount(
    task: WfTaskView,
    presentation: PrefillPresentation,
    suggestedByTrait: Map<String, Set<String>>,
    valuesByTrait: Map<String, Map<String, Any?>>,
    resolvedTraits: Set<String> = emptySet(),
): Int {
    var pending = 0
    for (trait in task.traits) {
        if (trait.traitId in resolvedTraits) continue
        val modes = presentation.modes[trait.traitId] ?: continue
        val suggested = suggestedByTrait[trait.traitId] ?: emptySet()
        val values = valuesByTrait[trait.traitId] ?: emptyMap()
        for ((field, mode) in modes) {
            val isPending = if (mode == SLDM.offer) isBlankPrefillValue(values[field]) else field in suggested
            if (isPending) pending++
        }
    }
    return pending
}

/** The fields of [traitId] a form starts marking as suggested (issue #710): its `filled` defaults, shown as
 *  suggestions until the user touches them. An `offer` field is not seeded, so it is not "suggested" -- it is
 *  offered -- and is absent here. Empty when the trait carries no default. */
fun suggestedFilledFields(presentation: PrefillPresentation, traitId: String): Set<String> =
    presentation.modes[traitId]?.filterValues { it == SLDM.filled }?.keys ?: emptySet()

/**
 * What a save sends of a trait's [payload] under its [layout] (issue #1071): only the fields an `authoritative` layout
 * lists -- the ones the form shows, which an edit save writes, refusing any other -- and the whole payload otherwise.
 * Pure.
 */
fun shownPayload(payload: Map<String, Any?>, layout: SchLayout?): Map<String, Any?> =
    if (layout?.mode == SchLayoutMode.authoritative) payload.filterKeys { it in layout.fieldNames } else payload

/**
 * The `entries` a save posts, from the values collected per trait (issue #536): each is a `{traitId, data}`
 * entry, the shape the save endpoint stores. A trait with no values collected is still sent as an empty entry,
 * so the gate sees it as present-but-empty rather than missing only because the page skipped it — the same
 * presence rule the backend applies.
 */
fun workflowSaveEntries(valuesByTrait: Map<String, Map<String, Any?>>): List<Map<String, Any?>> =
    valuesByTrait.map { (traitId, data) -> mapOf(GE.traitId to traitId, GE.data to data) }

/**
 * The body a workflow save posts: which workflow, task and save, and the collected entries. A survey `edit`
 * save (issue #659) also carries the [gedraId] of the form it updates; a create save omits it (null).
 */
fun workflowSaveBody(
    workflowId: String,
    taskId: String,
    saveId: String,
    entries: List<Map<String, Any?>>,
    gedraId: String? = null,
    forUserRef: String? = null,
): Map<String, Any?> = buildMap {
    put(GDF.workflowId, workflowId)
    put(GDF.taskId, taskId)
    put(GDF.saveId, saveId)
    put(GDF.entries, entries)
    gedraId?.let { put(GDF.gedraId, it) }
    // Create the form for another user (issue #727): a create save only, so a picked user rides the body just
    // as the plain create sends `user`; an edit save passes null.
    forUserRef?.trim()?.ifEmpty { null }?.let { put(EI.user, it) }
}

/**
 * The outcome of a save: whether it happened, the required trait ids left unmet, and -- on a create -- the created
 * form, whose id the page returns to the listing with. An edit carries none of the form (issue #827): it says only
 * that the form's state may have moved, and the page re-reads the view to show what changed.
 */
class WorkflowSaveOutcome(
    val saved: Boolean,
    val unmetTraits: List<String>,
    val item: Map<String, Any?>,
)

/** Reads a `/gedra/workflow/save` `results` map into a [WorkflowSaveOutcome]. */
fun parseSaveOutcome(results: Map<String, Any?>): WorkflowSaveOutcome = WorkflowSaveOutcome(
    saved = results[WSF.saved] == true,
    unmetTraits = results[WSF.unmetTraits].toJsonListOfStrings(),
    item = results[WSF.item].toJsonMapOrEmpty(),
)

/**
 * A trait locked for this caller on a form (issue #857): which workflow locks it ([workflowId], [label]) and whether
 * this caller may override the lock with a reason ([canOverride]).
 */
class TraitLock(
    val traitId: String,
    val workflowId: String,
    val label: String,
    val canOverride: Boolean,
    /** The trait's name as the server's refusals give it, by the rule every page names a trait with (issue #1520). */
    val name: String = humanizeFieldName(traitId),
) {
    /** How a lock message names this lock -- the wording the backend's refusals share ([TraitLockCopy]). */
    val names: LockNames get() = LockNames(name, label)
}

/**
 * The locks endpoint's answer (issues #857, #1433): the traits locked for this caller, and whether the whole form is
 * ([keptToOwner]) -- kept to its owner from them.
 */
class FormLocks(val traits: List<TraitLock> = emptyList(), val keptToOwner: Boolean = false)

/** The locks endpoint's results as [FormLocks] (issue #1433). Pure, and covered under `jsNodeTest`. */
fun parseFormLocks(results: Map<String, Any?>): FormLocks =
    FormLocks(parseTraitLocks(results[WVF.lockedTraits]), results[WVF.keptToOwner] == true)

/** A view's or the locks endpoint's `lockedTraits` (issue #857). Pure, and covered under `jsNodeTest`. */
fun parseTraitLocks(raw: Any?): List<TraitLock> = raw.toJsonListOfMaps().mapNotNull { l ->
    TraitLock(
        traitId = l[WFD.traitId].toOptStr() ?: return@mapNotNull null,
        workflowId = l[WFD.workflowId].toOptStr().orEmpty(),
        label = l[WFD.label].toOptStr().orEmpty(),
        canOverride = l[WVF.canOverride] == true,
        name = l[WVF.traitName].toOptStr() ?: humanizeFieldName(l[WFD.traitId].toOptStr().orEmpty()),
    )
}
